import { useCallback, forwardRef } from 'react'
import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import cx from 'classnames'
import Link from 'next/link'

import ListingBadge from '../ListingBadge'
import FavoriteButton from '../ListingContent/FavoriteButton'
import {
  ATTRIBUTION_CONFIGS,
  FeedAttributionConfig,
} from '../ListingContent/attribution/config'
import { MlsMark } from '../ListingContent/attribution/MlsMark'

import { AwardIcon, LockIcon } from 'icons'
import { useRouter } from 'next/router'

export interface IListingCardProps {
  id: number
  imgUrl?: string
  info: string
  address: string
  monthlyPayment: string
  /**
   * Suppresses the "From" / "/mo" scaffolding so a placeholder such as
   * "Contact Us For Pricing" reads as prose rather than a price. The caller
   * decides via `selectPayment`; the card does not inspect the payload.
   */
  monthlyPaymentUnavailable?: boolean
  existingOffer?: boolean
  reserved?: boolean
  boughtWithAcre?: boolean
  onClick?: () => void
  onSave: () => void
  border?: boolean
  isFavorite?: boolean
  listPrice: string
  feedName?: string
}

export interface IListingCardStaticProps {
  imgUrl?: string
  info: string
  address: string
  listPrice: string
  monthlyPayment: string
  /** See `IListingCardProps.monthlyPaymentUnavailable`. */
  monthlyPaymentUnavailable?: boolean
  border?: boolean
  feedName?: string
}

const ListingCard = forwardRef<HTMLAnchorElement, IListingCardProps>(
  (
    {
      id,
      imgUrl,
      info,
      address,
      monthlyPayment,
      monthlyPaymentUnavailable,
      listPrice,
      existingOffer,
      reserved,
      boughtWithAcre,
      onClick,
      onSave,
      border,
      isFavorite,
      feedName,
    },
    ref
  ) => {
    const { t } = useTranslation(['homes', 'common'])

    const router = useRouter()

    return (
      <li
        className={cx(
          'relative flex flex-col justify-between rounded-[15px] bg-white shadow-sm focus-within:ring-2 focus-within:ring-teal-3',
          {
            'ring-1 ring-green-1': border,
          }
        )}
      >
        <Link
          href={{
            pathname: router.pathname,
            query: {
              ...router.query,
              listing: id,
            },
          }}
          shallow
          passHref
        >
          <a
            onClick={onClick}
            data-testid="listing-card"
            ref={ref}
            className={cx(
              'group block text-left text-grey-8 ring-0',
              'after:absolute after:inset-0 after:cursor-pointer after:rounded-[15px] after:transition-shadow',
              'after:hover:ring-2 after:hover:ring-teal-3'
            )}
          >
            <Figure border={border} imgUrl={imgUrl} feedName={feedName} />
            {existingOffer ? (
              <ListingBadge
                text={t('offer-pending-label', { ns: 'common' })}
                icon={LockIcon}
                className="absolute left-16 top-16"
              />
            ) : reserved ? (
              <ListingBadge
                text={t('featured-label', { ns: 'common' })}
                icon={AwardIcon}
                className="absolute left-16 top-16"
              />
            ) : boughtWithAcre ? (
              <ListingBadge
                text={t('acre-bought-label', { ns: 'common' })}
                icon={AwardIcon}
                className="absolute left-16 top-16"
              />
            ) : null}
            <Summary info={info} address={address} />
          </a>
        </Link>
        <DetailsBar
          right={
            <div className="flex items-center justify-center">
              <FavoriteButton
                className="z-10"
                active={isFavorite}
                listingId={id}
                trackLinkLocation="Listing Card"
                onSave={onSave}
              />
            </div>
          }
        >
          <MonthlyPayment
            monthlyPayment={monthlyPayment}
            unavailable={monthlyPaymentUnavailable}
          />
          <Price price={listPrice} />
        </DetailsBar>
      </li>
    )
  }
)

export const ListingCardStatic = ({
  imgUrl,
  info,
  address,
  listPrice,
  monthlyPayment,
  monthlyPaymentUnavailable,
  border,
  feedName,
}: IListingCardStaticProps) => {
  return (
    <div
      className={cx(
        'relative flex flex-col justify-between rounded-[15px] bg-white shadow-sm focus-within:ring-2 focus-within:ring-teal-3',
        {
          'ring-1 ring-green-1': border,
        }
      )}
    >
      <div>
        <Figure border={border} imgUrl={imgUrl} feedName={feedName} />
        <Summary info={info} address={address} />
      </div>
      <div className="hidden lg:block">
        <StaticDetailsBar>
          <MonthlyPayment
            monthlyPayment={monthlyPayment}
            unavailable={monthlyPaymentUnavailable}
          />
          <Price price={listPrice} />
        </StaticDetailsBar>
      </div>
    </div>
  )
}

const Figure = ({
  border,
  imgUrl,
  feedName,
}: {
  border?: boolean
  imgUrl?: string
  feedName?: string
}) => {
  return (
    <figure
      className={cx('relative h-[176px]', {
        '-m-px': border,
      })}
    >
      <Image
        src={imgUrl ?? '/app-assets/listing-card-default.jpg'}
        className="rounded-t-[15px]"
        quality={100}
        alt=""
        layout="fill"
        objectFit="cover"
      />
      {feedName ? <FigureMlsMark feedName={feedName} /> : null}
    </figure>
  )
}

// Triad 18.2 (F)/(G): the feed's icon must sit adjacent to the property info
// on thumbnails. Photo-corner placement on a solid white backing mirrors
// Zillow's live Triad display and keeps the mark readable against any photo;
// formal Triad confirmation PENDING (ACR-18) — revisit if Triad reads
// "adjacent" as requiring proximity to the beds/baths line. Rendered at the
// config's thumbnail size (the asset's intrinsic 1x — the detail-page
// half-size render was illegible here). The white backing sits on the <img>
// itself, so MlsMark's hide-on-error hides the whole badge, not just the
// mark inside an empty white box; box-content because preflight's border-box
// would let the padding shrink the explicit width/height. Explicit allowlist
// — no config or no thumbnailIcon flag renders exactly the pre-existing
// output (see attribution/config.ts). Bottom-right only: ListingBadge owns
// the top-left corner.
const FigureMlsMark = ({ feedName }: { feedName: string }) => {
  const config: FeedAttributionConfig | undefined =
    ATTRIBUTION_CONFIGS[feedName]

  if (!config?.thumbnailIcon) {
    return null
  }

  return (
    <MlsMark
      config={config}
      feedName={feedName}
      width={config.thumbnailMarkWidth}
      height={config.thumbnailMarkHeight}
      className="absolute bottom-8 right-8 box-content rounded-sm bg-white p-4"
    />
  )
}

const Summary = ({ info, address }: { info: string; address: string }) => {
  const removeTextOrphan = useCallback((str: string) => {
    const wordArray = str.split(' ')
    if (wordArray.length > 1) {
      wordArray[wordArray.length - 2] +=
        '&nbsp;' + wordArray[wordArray.length - 1]
      wordArray.pop()
    }
    return wordArray.join(' ')
  }, [])

  return (
    <div className="w-full grow px-16 py-8 text-center lg:text-left">
      <p>
        <strong className="text-16 font-semibold leading-6 text-[#2D2D2D]">
          {info}
        </strong>
        <span
          className="block truncate text-14 leading-5 text-[#5C5C5C]"
          dangerouslySetInnerHTML={{
            __html: removeTextOrphan(address),
          }}
        />
      </p>
    </div>
  )
}

const MonthlyPayment = ({
  monthlyPayment,
  unavailable,
}: {
  monthlyPayment: string
  unavailable?: boolean
}) => {
  const { t } = useTranslation(['homes'])

  return (
    <div className="">
      <dt className="text-12 font-semibold leading-4 text-[#4b4e40]">
        <span className="sr-only">{t('monthlyPayment')}</span>
        {unavailable ? null : t('from')}
      </dt>
      <dd
        className="text-18 font-semibold leading-7 text-grey-8"
        data-testid="card-monthly-payment"
      >
        {monthlyPayment}
        {unavailable ? null : (
          <span className="text-16" aria-hidden="true">
            {' '}
            /{t('monthAbbreviation')}
          </span>
        )}
      </dd>
    </div>
  )
}

const Price = ({ price }: { price: string }) => {
  const { t } = useTranslation(['homes'])

  return (
    <div className="">
      <dt className="text-12 font-semibold leading-4 text-[#4b4e40]">
        {t('price')}
      </dt>
      <dd className="text-18 font-semibold leading-7 text-grey-8">{price}</dd>
    </div>
  )
}

const DetailsBar = ({
  children,
  right,
}: {
  children: React.ReactNode
  right?: React.ReactNode
}) => {
  return (
    <dl className="flex w-full gap-12 border-t p-16">
      <div className="flex grow justify-around text-center leading-tight sm:text-left">
        {children}
      </div>
      {right ? <div className="flex">{right}</div> : null}
    </dl>
  )
}

const StaticDetailsBar = ({ children }: { children: React.ReactNode }) => {
  return (
    <dl className="grid w-full grid-cols-2 border-t p-16 text-center leading-tight sm:text-left">
      {children}
    </dl>
  )
}

ListingCard.displayName = 'ListingCard'

export default ListingCard
